import { useState, useEffect, useCallback } from 'react';
import { useData } from '../DataContext';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import api from '../api';
import Modal from '../components/Modal';
import useModal from '../components/useModal';
import Segmented from '../components/Segmented';
import Loader from '../components/Loader';

const COLORS = ['var(--accent-solid)','var(--teal)','var(--accent-2)','#c4b5fd','#5eead4','#c4b5fd','#a78bfa','#67e8f9'];
const MONTOS_RAPIDOS = [50000, 100000, 200000, 500000];
const PERIODOS = [
  { label: 'Hoy', value: 'hoy' },
  { label: '7 días', value: '7d' },
  { label: '30 días', value: '30d' },
  { label: 'Personalizado', value: 'custom' },
];

function exportCSV(data, filename) {
  if (!data?.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(r => keys.map(k => JSON.stringify(r[k] ?? '')).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
}

export default function Billing() {
  const { billing: billingBase, refrescar } = useData();
  const { modal } = useModal();
  const [monto, setMonto]               = useState('');
  const [loadingLink, setLoadingLink]   = useState(false);
  const [linkPago, setLinkPago]         = useState(null);
  const [error, setError]               = useState('');
  const [periodo, setPeriodo]           = useState('30d');
  const [fechaDesde, setFechaDesde]     = useState('');
  const [fechaHasta, setFechaHasta]     = useState('');
  const [campanasFiltro, setCampanasFiltro] = useState([]);
  const [billingFiltrado, setBillingFiltrado] = useState(null);
  const [loadingFiltro, setLoadingFiltro] = useState(false);

  const getFechaDesde = useCallback(() => {
    const d = new Date();
    if (periodo === 'hoy') { d.setHours(0,0,0,0); return d.toISOString().split('T')[0]; }
    if (periodo === '7d') { d.setDate(d.getDate()-7); return d.toISOString().split('T')[0]; }
    if (periodo === '30d') { d.setDate(d.getDate()-30); return d.toISOString().split('T')[0]; }
    return fechaDesde;
  }, [periodo, fechaDesde]);

  const cargarFiltrado = useCallback(async () => {
    setLoadingFiltro(true);
    try {
      const params = new URLSearchParams();
      const desde = getFechaDesde();
      if (desde) params.append('fecha_desde', desde);
      if (periodo === 'custom' && fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (campanasFiltro.length === 1) params.append('campana_id', campanasFiltro[0]);
      const r = await api.get(`/billing/summary?${params.toString()}`);
      setBillingFiltrado(r.data);
    } catch(e) { console.error(e); }
    finally { setLoadingFiltro(false); }
  }, [periodo, fechaDesde, fechaHasta, campanasFiltro, getFechaDesde]);

  useEffect(() => { cargarFiltrado(); }, [cargarFiltrado]);

  const toggleCampana = (id) => {
    setCampanasFiltro(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const generarLink = async () => {
    if (!monto || parseInt(monto) < 50000) { setError('El monto mínimo es $50.000 COP'); return; }
    setError('');
    setLinkPago(null);
    // La pestaña se abre YA, dentro del clic. Si se abre después de esperar al servidor (~2 s),
    // el navegador la bloquea como ventana emergente (sobre todo en celular) y no pasa nada.
    const ventana = window.open('', '_blank');
    try { ventana && ventana.document.write('<p style="font-family:sans-serif;padding:24px;color:#555">Generando el link de pago de Wompi…</p>'); } catch (e) {}
    setLoadingLink(true);
    try {
      const r = await api.post('/billing/recargar', { monto: parseInt(monto) });
      const link = r.data.link_de_pago;
      setLinkPago(link);
      if (ventana && !ventana.closed) ventana.location.href = link;
      else window.location.assign(link);   // el navegador no dejó abrir pestaña: se va en esta
      setMonto('');
      setTimeout(refrescar, 5000);
    } catch (err) {
      try { ventana && ventana.close(); } catch (e) {}
      setError(err.response?.data?.detail || 'Error generando el link de pago');
    } finally { setLoadingLink(false); }
  };

  const billing = billingFiltrado || billingBase;
  if (!billing) return <Loader full />;

  const consumo = billing.consumo_mes || {};
  const campanas = billingBase?.campanas || [];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Inter,sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Facturación</h1>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 4 }}>Saldo y consumo de tu cuenta</p>
      </div>

      {/* Saldo + recarga */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 20, marginBottom: 24 }}>
        <div style={{ background: 'linear-gradient(135deg,var(--accent-soft),rgba(109,40,217,0.1))', border: '1.5px solid var(--accent-soft-2)', borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 11, color: 'var(--accent-solid)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Saldo disponible</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
            ${(billingBase?.saldo_actual || 0).toLocaleString('es-CO', { minimumFractionDigits: 2 })} <span style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 400 }}>COP</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, marginBottom: 16 }}>Tarifa: ${billingBase?.tarifa_por_minuto} COP/min</div>

          {/* Montos rápidos */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {MONTOS_RAPIDOS.map(m => (
              <button key={m} onClick={() => setMonto(String(m))} style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                border: monto === String(m) ? '1.5px solid var(--accent-solid)' : '1px solid var(--surface-2)',
                background: monto === String(m) ? 'var(--accent-soft)' : 'var(--surface-3)',
                color: monto === String(m) ? 'var(--accent)' : 'var(--text-3)',
                transition: 'all 0.15s',
              }}>${(m/1000).toFixed(0)}k</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <input type="number" value={monto} onChange={e => setMonto(e.target.value)} placeholder="Otro monto..."
              style={{ flex: 1, minWidth: 0, padding: '9px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface-2)', borderRadius: 9, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
            <button onClick={generarLink} disabled={loadingLink}
              style={{ padding: '9px 18px', background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {loadingLink ? '...' : 'Recargar →'}
            </button>
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{error}</div>}
          {linkPago && <div style={{ fontSize: 12, marginTop: 6 }}><a href={linkPago} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>¿No se abrió el pago? Ábrelo aquí en Wompi →</a></div>}
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>Recarga mínima $50.000 COP · Pago seguro vía Wompi</div>
        </div>


      </div>

      {/* Filtros + resumen */}
      <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, marginBottom: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Período */}
          <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>Período</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Segmented options={PERIODOS} value={periodo} onChange={setPeriodo} size="sm" />
              {loadingFiltro && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>↻</span>}
              <button onClick={() => exportCSV(billingBase?.ultimas_recargas?.map(r => ({ fecha: r.fecha, monto: r.monto, estado: r.estado })), 'recargas.csv')}
                style={{ marginLeft: 'auto', padding: '6px 14px', background: 'transparent', border: '1px solid var(--surface-2)', borderRadius: 20, color: 'var(--text-3)', fontSize: 11, cursor: 'pointer' }}>
                ↓ Exportar recargas
              </button>
            </div>
            {periodo === 'custom' && (
              <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
                <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
                  style={{ padding: '7px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface-2)', borderRadius: 8, color: 'var(--text)', fontSize: 12 }} />
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>→</span>
                <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
                  style={{ padding: '7px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface-2)', borderRadius: 8, color: 'var(--text)', fontSize: 12 }} />
              </div>
            )}
          </div>

          {/* Campañas */}
          {campanas.length > 0 && (
            <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: '14px 18px' }}>
              <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>Campañas</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxHeight: 124, overflowY: 'auto' }}>
                {campanas.map(c => {
                  const activo = campanasFiltro.includes(c.id);
                  return (
                    <button key={c.id} onClick={() => toggleCampana(c.id)} style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: activo ? '1.5px solid var(--accent-solid)' : '1px solid var(--surface-2)',
                      background: activo ? 'var(--accent-soft)' : 'transparent',
                      color: activo ? 'var(--accent)' : 'var(--text-3)',
                      transition: 'all 0.15s',
                    }}>{c.nombre}</button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Tarjeta resumen del período */}
        <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 20, minWidth: 200 }}>
          <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 14 }}>Resumen período</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 2 }}>LLAMADAS</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{consumo.llamadas_count || 0}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 2 }}>MINUTOS</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{consumo.minutos_consumidos || 0}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 2 }}>COSTO</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>${(consumo.costo_total || 0).toLocaleString('es-CO')} <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 400 }}>COP</span></div>
            </div>
            <button onClick={() => exportCSV(consumo.por_dia?.map(d => ({ fecha: d.fecha, llamadas: d.llamadas, costo: d.costo })), 'informe_consumo.csv')}
              style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--surface-2)', borderRadius: 8, color: 'var(--text-3)', fontSize: 11, cursor: 'pointer', marginTop: 4 }}>
              ↓ Exportar informe
            </button>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {consumo.por_dia?.length > 0 && (
          <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>Costo por día</div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={consumo.por_dia}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-2)" />
                <XAxis dataKey="fecha" tick={{ fill: 'var(--text-3)', fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text-3)', fontSize: 9 }} axisLine={false} tickLine={false} width={50} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`$${v.toLocaleString('es-CO')} COP`, 'Costo']}
                  contentStyle={{ background: 'var(--bg)', border: '1px solid var(--surface-2)', borderRadius: 8, fontSize: 11 }} />
                <Line type="monotone" dataKey="costo" stroke="var(--accent-solid)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {consumo.por_campana?.length > 0 && (
          <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>Distribución por campaña</div>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={consumo.por_campana} dataKey="costo" nameKey="campana" cx="50%" cy="50%" innerRadius={45} outerRadius={70}>
                  {consumo.por_campana.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => [`$${v.toLocaleString('es-CO')} COP`, 'Costo']}
                  contentStyle={{ background: 'var(--bg)', border: '1px solid var(--surface-2)', borderRadius: 8, fontSize: 11 }} />
                {(consumo.por_campana || []).length <= 8 && <Legend iconSize={8} wrapperStyle={{ fontSize: 10, color: 'var(--text-3)' }} />}
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {consumo.por_campana?.length > 0 && (
        <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>Costo por campaña</div>
          <ResponsiveContainer width="100%" height={Math.max(80, consumo.por_campana.length * 36)}>
            <BarChart data={consumo.por_campana} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-2)" horizontal={false} />
              <XAxis type="number" tick={{ fill: 'var(--text-3)', fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="campana" tick={{ fill: 'var(--text-2)', fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
              <Tooltip formatter={v => [`$${v.toLocaleString('es-CO')} COP`, 'Costo']}
                contentStyle={{ background: 'var(--bg)', border: '1px solid var(--surface-2)', borderRadius: 8, fontSize: 11 }} />
              <Bar dataKey="costo" fill="var(--accent-solid)" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Historial recargas */}
      <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '.08em' }}>Historial de recargas</div>
        {(billingBase?.ultimas_recargas || []).length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Sin recargas registradas</div>}
        {(billingBase?.ultimas_recargas || []).map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--surface-3)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>${parseInt(r.monto).toLocaleString('es-CO')} COP</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.date_created ? new Date(r.date_created).toLocaleDateString('es-CO', { day:'2-digit', month:'long', year:'numeric' }) : ''}</div>
            </div>
            <span style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 20,
              color: r.status === 'APROBADA' ? 'var(--success)' : r.status === 'RECHAZADA' ? 'var(--danger)' : 'var(--warning)',
              background: r.status === 'APROBADA' ? 'var(--success-soft)' : r.status === 'RECHAZADA' ? 'var(--danger-soft)' : 'rgba(250,204,21,0.1)',
              border: r.status === 'APROBADA' ? '1px solid rgba(74,222,128,0.2)' : r.status === 'RECHAZADA' ? '1px solid var(--danger-soft)' : '1px solid rgba(250,204,21,0.2)',
            }}>
              {r.status?.toUpperCase() || 'PENDIENTE'}
            </span>
          </div>
        ))}
      </div>
      <Modal {...modal} />
    </div>
  );
}
